import { useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';

import { fetchChats } from '../../api/chats';

import type { ChatsResponse, UseChatsOptions } from './types';

export const CHATS_QUERY_KEY = 'chats';

/**
 * Re-overlay client-side title state on top of a fresh server response.
 *
 * Two kinds of locally-tracked rows exist only in the React Query cache:
 * `isOptimistic` (injected on the first transcript, server has never returned
 * the id) and `titlePending` (session ended, server returns the row but not
 * yet its title). A refetch that replaced the cache wholesale would drop the
 * first and blank the second — so:
 *
 * - tracked rows the server has NOT returned survive, prepended (newest-first,
 *   matching the injection order);
 * - a server row with an EMPTY title never clobbers a tracked row's locally
 *   generated title: the local title holds and the row becomes `titlePending`
 *   until the server confirms a real one (CLOUD-612);
 * - a server row with a real title wins outright — all local flags drop.
 *
 * Pure: same input always yields the same output; identity-returns the server
 * response when there is nothing to merge so React Query's structural sharing
 * is preserved.
 */
export function mergeOptimisticChats(previous: ChatsResponse | undefined, server: ChatsResponse): ChatsResponse {
  const tracked = (previous?.chats ?? []).filter((c) => c.isOptimistic || c.titlePending);
  if (tracked.length === 0) return server;

  const missing = tracked.filter((c) => !server.chats.some((s) => s.id === c.id));
  let heldTitle = false;
  const chats = server.chats.map((s) => {
    const local = tracked.find((c) => c.id === s.id);
    if (!local || s.title || !local.title) return s;
    heldTitle = true;
    return { ...s, title: local.title, titlePending: true };
  });

  if (missing.length === 0 && !heldTitle) return server;
  return {
    ...server,
    chats: [...missing, ...chats],
    total: server.total + missing.length,
  };
}

/**
 * Hook to fetch chats for the authenticated user.
 * Uses react-query for caching, background refetching, and deduplication.
 *
 * The queryFn merges still-pending optimistic rows over the server response
 * (see `mergeOptimisticChats`) so an in-flight optimistic entry is
 * never wiped by a stale/partial refetch and reconciles to the real record
 * once the backend confirms it.
 */
export function useChats(options: UseChatsOptions = {}) {
  const { limit = 50, offset = 0, enabled = true } = options;
  const queryClient = useQueryClient();

  return useQuery<ChatsResponse, Error>({
    queryKey: [CHATS_QUERY_KEY, { limit, offset }],
    queryFn: async () => {
      const server = await fetchChats(limit, offset);
      const previous = queryClient.getQueryData<ChatsResponse>([CHATS_QUERY_KEY, { limit, offset }]);
      return mergeOptimisticChats(previous, server);
    },
    enabled,
    // Keep the current rows visible while a larger `limit` page loads (the
    // sidebar grows its limit on scroll) so the list never flashes empty.
    placeholderData: keepPreviousData,
    // Never auto-refetch on navigation/remount/focus. The list stays current via
    // explicit invalidation only: optimistic row injection on a new chat
    // (chat-provider) and the session-end refetch (chats-refetch). Everything
    // else reuses the cache, so changing pages no longer flashes a reload.
    staleTime: Infinity,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
    gcTime: 30 * 60 * 1000, // keep cache across navigation within a session
  });
}
